import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'react-qr-code';
import Clone from '../../icons/Clone';
import LockClosed from '../../icons/LockClosed';
import Globe from '../../icons/Globe';
import UserGroup from '../../icons/UserGroup';
import ChevronRight from '../../icons/ChevronRight';
import AccessRequestsTable from './AccessRequestsTable';

type DossierVisibility = 'private' | 'public' | 'on_request';

interface VisibilitySettingsProps {
  visibility: string;
  onVisibilityChange: (value: string) => void;
  dossierId?: string;
  coAuthorsCanManageAccess?: boolean;
  onCoAuthorsCanManageAccessChange?: (value: boolean) => void;
}

const VISIBILITY_OPTIONS: {
  value: DossierVisibility;
  label: string;
  icon: React.FC<any>;
  description: string;
}[] = [
  {
    value: 'private',
    label: 'Privé',
    icon: LockClosed,
    description: 'Seulement vous et les co-auteurs',
  },
  {
    value: 'public',
    label: 'Public',
    icon: Globe,
    description: 'Visible par tous',
  },
  {
    value: 'on_request',
    label: 'Sur demande',
    icon: UserGroup,
    description: 'Les utilisateurs connectés peuvent demander un accès',
  },
];

const VisibilitySettings: React.FC<VisibilitySettingsProps> = ({
  visibility,
  onVisibilityChange,
  dossierId,
  coAuthorsCanManageAccess = false,
  onCoAuthorsCanManageAccessChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentUrl = window.location.href;

  const activeOption = VISIBILITY_OPTIONS.find((o) => o.value === visibility) || VISIBILITY_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (value: DossierVisibility) => {
    onVisibilityChange(value);
    setIsOpen(false);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  const handleQrClick = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const ActiveIcon = activeOption.icon;

  return (
    <div>
      <p className="text-base font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
        Paramètres de visibilité du dossier
      </p>

      {/* Dropdown trigger + menu */}
      <div className="relative" ref={dropdownRef}>
        {/* Trigger button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-300 bg-white dark:bg-mcdm-third-500 hover:border-mcdm-third-300 dark:hover:border-mcdm-third-200 transition-colors"
        >
          <div className="flex items-center gap-3">
            <ActiveIcon
              width="20px"
              height="20px"
              fill="currentColor"
              className="text-mcdm-third-400 dark:text-mcdm-primary-100 flex-shrink-0"
            />
            <div className="text-left">
              <span className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                {activeOption.label}
              </span>
              <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300 mt-0.5">
                {activeOption.description}
              </p>
            </div>
          </div>
          <ChevronRight
            width="16px"
            height="16px"
            fill="currentColor"
            className={`text-mcdm-third-300 dark:text-mcdm-primary-300 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
          />
        </button>

        {/* Dropdown menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 right-0 mt-2 bg-white dark:bg-mcdm-third-500 border border-mcdm-primary-200 dark:border-mcdm-third-300 rounded-lg shadow-lg z-50 overflow-hidden"
            >
              <div className="p-1.5">
                {VISIBILITY_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isActive = option.value === visibility;

                  return (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                      className={`
                        w-full flex items-start gap-3 p-3 rounded-md transition-colors text-left
                        hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-400
                        ${isActive ? 'bg-mcdm-primary-200 dark:bg-mcdm-third-300' : ''}
                      `}
                    >
                      <Icon
                        width="20px"
                        height="20px"
                        fill="currentColor"
                        className="mt-0.5 flex-shrink-0 text-mcdm-third-400 dark:text-mcdm-primary-100"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                            {option.label}
                          </span>
                          {isActive && (
                            <span className="text-xs text-mcdm-vert-400 font-semibold">
                              
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300 mt-0.5">
                          {option.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Toggle co-auteurs pour on_request */}
      {visibility === 'on_request' && onCoAuthorsCanManageAccessChange && (
        <div className="mt-6">
          <label className="flex items-start cursor-pointer group">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={coAuthorsCanManageAccess}
                onChange={(e) => onCoAuthorsCanManageAccessChange(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mcdm-green-400/20 dark:peer-focus:ring-mcdm-green-400/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-mcdm-green-400"></div>
            </div>
            <div className="ml-3 text-sm">
              <span className="font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 group-hover:text-mcdm-secondary-400 transition-colors">
                Les co-auteurs peuvent gérer les demandes d'accès
              </span>
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                Si activé, les co-auteurs pourront accepter ou refuser les demandes d'accès au dossier.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Table de gestion des accès pour on_request */}
      {visibility === 'on_request' && dossierId && (
        <div className="mt-6">
          <h4 className="text-sm uppercase tracking-wider text-mcdm-third-300 dark:text-mcdm-primary-200 font-semibold mb-4 border-b border-mcdm-primary-200 dark:border-mcdm-third-300 pb-2">
            Demandes d'accès
          </h4>
          <AccessRequestsTable dossierId={dossierId} />
        </div>
      )}

      {/* QR code et partage pour public et on_request */}
      {(visibility === 'public' || visibility === 'on_request') && (
        <div className="mt-6">
          <h3 className="text-base font-semibold mb-4 text-mcdm-third-500 dark:text-mcdm-primary-100">
            Partage par QR code
          </h3>
          <div className="flex items-center">
            <QRCode value={currentUrl} size={120} onClick={handleQrClick} style={{ cursor: 'pointer' }} />
          </div>
          <div className="mt-4">
            <button
              onClick={handleCopyUrl}
              className={`px-4 py-2 rounded text-white flex items-center justify-center ${
                copied ? 'bg-mcdm-vert-400' : 'bg-mcdm-vert-400 hover:bg-mcdm-vert-500'
              }`}
            >
              <Clone fill="white" width="20px" height="20px" className="mr-2" />
              {copied ? 'Copie réussie !' : "Copier l'adresse"}
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeModal}
        >
          <div className="bg-white p-8 rounded-lg relative" onClick={(e) => e.stopPropagation()}>
            <QRCode value={currentUrl} size={500} className="mt-0" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VisibilitySettings;
